import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, ExternalLink, Github } from 'lucide-react';
import { projects } from '../data/mockData';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const Projects = () => {
  return (
    <section id="projects" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Featured <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Projects</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Some of my recent work and deployed applications
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-10">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, boxShadow: "0 8px 24px rgba(6,182,212,0.18)", borderColor: "#06b6d4" }}
              className="group relative bg-gray-800/80 glass rounded-2xl overflow-hidden border-2 border-gray-700 hover:border-cyan-400/50 transition-all shadow-xl"
            >
              {/* Featured Badge */}
              {project.featured && (
                <div className="absolute top-5 right-5 z-10">
                  <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md border-0 px-4 py-2 font-bold tracking-wide text-sm uppercase">
                    Featured
                  </Badge>
                </div>
              )}

              {/* Project Preview */}
              <div className="relative h-64 overflow-hidden rounded-t-xl">
                <motion.img
                  whileHover={{ scale: 1.075 }}
                  src={project.previewImage || project.image}
                  alt={project.title}
                  className="w-full h-full object-cover z-0 transition-transform duration-200"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-cyan-700/25 via-transparent to-blue-900/45 opacity-70 z-10 pointer-events-none"></div>
              </div>

              {/* Project Details */}
              <div className="p-7">
                {(project.category || project.period) && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {project.category && (
                      <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-400/30">
                        {project.category}
                      </Badge>
                    )}
                    {project.period && (
                      <Badge className="bg-blue-500/10 text-blue-300 border-blue-400/30">
                        <Calendar size={14} className="mr-1" />
                        {project.period}
                      </Badge>
                    )}
                  </div>
                )}

                <h3 className="text-2xl font-extrabold text-white mb-3 group-hover:text-cyan-400 transition-colors tracking-tight">
                  {project.title}
                </h3>
                <p className="text-gray-400 mb-5 leading-relaxed text-base">{project.description}</p>

                {project.mission && (
                  <p className="text-cyan-300 mb-5 border-l-2 border-cyan-400/50 pl-3 text-sm font-medium">
                    {project.mission}
                  </p>
                )}

                {project.role && (
                  <p className="text-gray-300 mb-5 leading-relaxed text-sm">
                    <span className="text-cyan-400 font-semibold">Role:</span> {project.role}
                  </p>
                )}

                {project.features?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {project.features.slice(0, 5).map((feature) => (
                      <Badge
                        key={feature}
                        variant="secondary"
                        className="bg-gray-900/80 text-gray-300 border-gray-700 hover:bg-gray-800 px-3 py-1 text-xs rounded-full"
                      >
                        {feature}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {project.technologies.map((tech) => (
                    <Badge
                      key={tech}
                      variant="outline"
                      className="border-cyan-400/30 text-cyan-400 bg-gray-900/80 hover:bg-cyan-400/10 font-medium px-3 py-1 text-xs rounded-full"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end">
                  {project.liveLink && project.liveLink !== '#' && (
                    <Button
                      onClick={() => window.open(project.liveLink, '_blank')}
                      className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold py-2 px-5 transition shadow"
                    >
                      <ExternalLink size={16} />
                      View Live
                    </Button>
                  )}
                  {project.githubLink && (
                    <Button
                      onClick={() => window.open(project.githubLink, '_blank')}
                      variant="outline"
                      className="flex items-center gap-2 border-gray-600 text-gray-200 hover:bg-gray-700 font-semibold py-2 px-5 transition shadow"
                    >
                      <Github size={16} />
                      Code
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
